import React, { createContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { normalizeRole } from '../utils/roleUtils';

export const AuthContext = createContext();
const USER_STORAGE_KEY = 'user';
const TOKEN_STORAGE_KEY = 'auth_token';

const normalizeUser = (userData) => {
    if (!userData) return null;
    return { ...userData, role: normalizeRole(userData.role) };
};

const getStoredUser = () => {
    try {
        const rawUser = localStorage.getItem(USER_STORAGE_KEY);
        return rawUser ? normalizeUser(JSON.parse(rawUser)) : null;
    } catch (error) {
        console.error('User parsing error:', error);
        localStorage.removeItem(USER_STORAGE_KEY);
        return null;
    }
};

const persistUser = (userData) => {
    const normalizedUser = normalizeUser(userData);
    if (!normalizedUser) return;

    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(normalizedUser));
};

const persistToken = (token, rememberMe = false) => {
    if (!token) return;

    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(TOKEN_STORAGE_KEY, token);
};

const clearStoredAuth = () => {
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => getStoredUser());
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isActive = true;

        const syncAuth = async () => {
            const storedUser = getStoredUser();

            if (storedUser && isActive) {
                setUser(storedUser);
            }

            try {
                const { data } = await api.get('/auth/me');
                if (!isActive) return;

                const syncedUser = normalizeUser(data.user);
                setUser(syncedUser);
                setIsAuthenticated(true);
                persistUser(syncedUser);
            } catch (error) {
                if (error.response?.status !== 401) {
                    console.error('User sync error:', error);
                }
                clearStoredAuth();
                if (isActive) {
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        setLoading(true);
        syncAuth();

        return () => {
            isActive = false;
        };
    }, []);

    const login = (userData, token, rememberMe = false) => {
        const normalizedUser = normalizeUser(userData);
        persistUser(normalizedUser);
        persistToken(token, rememberMe);
        setUser(normalizedUser);
        setIsAuthenticated(true);
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout request failed:', error);
        }

        clearStoredAuth();
        setUser(null);
        setIsAuthenticated(false);
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
